import {
  Button,
  Checkbox,
  KeyboardShortcut,
  SearchInput,
  SegmentedControl,
  TooltipAnchor,
  TooltipHint as TooltipHintBase,
} from '@gnomad/ui'
import React, { useRef } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import styled from 'styled-components'

// @ts-expect-error ts-migrate(2307) FIXME: Cannot find module '@fortawesome/fontawesome-free/... Remove this comment to see the full error message
import Warning from '@fortawesome/fontawesome-free/svgs/solid/exclamation-triangle.svg'

import {
  alleleFrequencyFilterAtom,
  selectedVariantFieldsAtom,
  useToggleSelectedVariantField,
  membershipFiltersAtom,
  multiAnalysisColorByAtom,
  multiAnalysisTransparencyAtom,
  multiAnalysisVariantTableFormatAtom,
  variantFieldGroupState,
  VariantFieldType,
  useSelectedVariantFieldsPreset,
  selectedVariantFieldsOptions,
  variantSearchTextAtom,
  showCaseControlTracksAtom,
  autoPvalFilter,
  gwasCatalogOptionsAtom,
  enableVariantLabelsAtom,
  variantLabelsAtom,
  variantShowLabelAtom,
} from '../variantState'
// @ts-ignore: FIXME
import RangeSlider from '../PhenotypeList/RangeSlider'
import { getVariantColumns } from '../VariantList/variantTableColumns'
import { ColorMarker, ControlsHeader, ControlsHeaderTitle, ControlsCloseButton } from '../UserInterface'
import {
  variantGreenThreshold,
  greenThresholdColor,
  RoundedNumber,
  variantYellowThreshold,
  yellowThresholdColor,
} from '../PhenotypeList/Utils'
import { getAlleleFrequencyScale, consequenceCategoryColors } from './LocusPagePlots'
import { ancestryGroupAtom, regionIdAtom, variantIdAtom, locusMafAtom, MafOption, hideGeneOptsAtom, mafSignificanceAtom, AnnotationCategory } from '../sharedState'
import { useAppNavigation } from '../hooks/useAppNavigation'
import { getDetailOptionsLabel, optionPanelContract } from '../browserUiContracts'

const TooltipHint = styled(TooltipHintBase)`
  background-image: none;
`

const ControlsHeaderWrapper = styled(ControlsHeader)`
  grid-area: controls-header;
`

const GenePageControlsGeneFocus = styled.div<{ $embedded?: boolean }>`
  box-sizing: border-box;
  flex: 0 0 230px;
  width: 230px;
  min-width: 0;
  max-width: 230px;
  margin-left: 5px;
  height: ${({ $embedded }) => ($embedded ? 'auto' : '100%')};
  min-height: 0;
  max-height: ${({ $embedded }) => ($embedded ? 'none' : '100%')};
  align-self: ${({ $embedded }) => ($embedded ? 'flex-start' : 'stretch')};
  padding-left: 0;
  padding-right: 0;

  overflow-y: ${({ $embedded }) => ($embedded ? 'visible' : 'auto')};
  overflow-x: ${({ $embedded }) => ($embedded ? 'visible' : 'hidden')};

  display: grid;
  grid-auto-rows: min-content;
  grid-template-columns: 1fr;
  grid-template-areas:
    'controls-header'
    'search'
    'maf-selector'
    'burden-set'
    'zoom-controls'
    'gwas-catalog-options'
    'show-case-control-tracks'
    'enable-variant-labels'
    'color-by'
    'pvalue-legend'
    'af-slider'
    'af-legend'
    'transparency-slider'
    'table-format'
    'field-group-controls'
    'individual-field-checkboxes'
    'app-data-stats';

  .maf-selector {
    grid-area: maf-selector;
  }

  grid-row-gap: 1.2em;
  align-items: flex-start;

  span {
    margin-right: 5px;
    margin-right: 5px;
    margin-bottom: 5px;
  }

  .multi-analysis-controls-title {
    grid-area: title;
  }

  .multi-analysis-variant-search {
    grid-area: search;
  }

  .burden-set {
    grid-area: burden-set;

    span {
      margin-right: 5px;
    }

    label {
      display: flex;
      flex-direction: row;

      .burden-set-text {
        min-width: 80px;
      }
    }
  }

  .gwas-catalog-options {
    grid-area: gwas-catalog-options;
  }

  .show-case-control-tracks {
    grid-area: show-case-control-tracks;
  }

  .enable-variant-labels {
    grid-area: enable-variant-labels;
  }

  .multi-analysis-color-by {
    grid-area: color-by;
  }

  .pvalue-slider {
    grid-area: pvalue-slider;
    text-align: center;
    display: flex;
    flex-direction: column;
  }
  .auto-pvalue-filter-warning {
    font-size: 10px;
  }
  .pvalue-legend {
    grid-area: pvalue-legend;
    text-align: center;
  }
  .allele-frequency-slider {
    grid-area: af-slider;
    text-align: center;
    margin-bottom: 1.3em;

    .display-values {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    .value {
      font-size: 10px;
      color: grey;
      margin-right: 3px;
      margin-left: 3px;
    }
  }
  .allele-frequency-legend {
    grid-area: af-legend;
    text-align: center;
  }
  .transparency-slider {
    grid-area: transparency-slider;
    text-align: center;
    margin-bottom: 0.8em;
  }
  .field-group-controls {
    grid-area: field-group-controls;
  }

  .table-format {
    grid-area: table-format;
    margin-top: 20px;
  }
  .individual-field-checkboxes {
    grid-area: individual-field-checkboxes;
    display: flex;
    width: 100%;
    flex-direction: column;
    flex-wrap: wrap;
    max-height: 300px;
  }

  .individual-checkbox {
    display: flex;
    flex-direction: row;
    max-width: 50%;

    span {
      text-decoration: underline;
      text-decoration-style: dashed;
      text-decoration-thickness: 1px;
      text-decoration-color: grey;
    }
  }

  .unselect-variant {
    grid-area: unselect-variant;
  }

  @media (max-width: 600px) {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    max-width: none;
    max-height: 38vh;
    align-self: auto;
    margin-left: 0;
    padding: 0 12px 12px;
    overflow: auto;
    grid-row-gap: 0.8em;
  }
`

const GenePageControlStylesVariantFocus = styled(GenePageControlsGeneFocus)`
  grid-template-areas:
    'controls-header'
    'unselect-variant'
    'burden-set'
    'show-case-control-tracks'
    'enable-variant-labels'
    'color-by'
    'transparency-slider'
    'table-format'
    'field-group-controls'
    'individual-field-checkboxes'
    'app-data-stats';
`

const MafOptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MafSignificanceDots = styled.div`
  display: flex;
  gap: 1px;
  margin-bottom: 4px;
  height: 10px;
  align-items: center;
  justify-content: center;
`

const SignificanceDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  border: 1px solid rgba(0, 0, 0, 0.3);
`

const EmptyDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: transparent;
  border: 1px solid rgba(0, 0, 0, 0.3);
`

const MafButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border: none;
  background: ${({ $active }) => ($active ? '#262262' : 'var(--theme-surface-alt, #f5f5f5)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--theme-text, #333)')};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-right: 1px solid var(--theme-border, #ccc);

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${({ $active }) => ($active ? '#262262' : 'var(--theme-border, #e0e0e0)')};
  }
`

// Category colors matching the variant coloring
const annotationColors: Record<AnnotationCategory, string> = {
  pLoF: consequenceCategoryColors.pLoF,
  missense: consequenceCategoryColors.missense,
  synonymous: consequenceCategoryColors.synonymous,
}

const annotationLabels: Record<AnnotationCategory, string> = {
  pLoF: 'pLoF',
  missense: 'Missense',
  synonymous: 'Synonymous',
}

const MafSelector: React.FC = () => {
  const [locusMaf, setLocusMaf] = useRecoilState(locusMafAtom)
  const mafSignificance = useRecoilValue(mafSignificanceAtom)

  const mafOptions: { value: MafOption; label: string }[] = [
    { value: 0.01, label: '1%' },
    { value: 0.001, label: '0.1%' },
    { value: 0.0001, label: '0.01%' },
  ]

  const annotationOrder: AnnotationCategory[] = ['pLoF', 'missense', 'synonymous']

  return (
    <div className='maf-selector'>
      <span>
        <strong>Max MAF</strong>
      </span>
      <div style={{ display: 'flex', gap: 0, marginTop: 4 }}>
        {mafOptions.map((opt) => {
          const sigForMaf = mafSignificance[opt.value]
          return (
            <MafOptionWrapper key={opt.value}>
              <MafSignificanceDots>
                {annotationOrder.map((annot) => {
                  const hasHit = sigForMaf[annot] !== 'none'
                  const tooltip = `${annotationLabels[annot]}: ${hasHit ? 'significant' : 'not significant'}`
                  return hasHit ? (
                    <SignificanceDot key={annot} $color={annotationColors[annot]} title={tooltip} />
                  ) : (
                    <EmptyDot key={annot} title={tooltip} />
                  )
                })}
              </MafSignificanceDots>
              <MafButton
                type="button"
                $active={locusMaf === opt.value}
                aria-pressed={locusMaf === opt.value}
                onClick={() => setLocusMaf(opt.value)}
              >
                {opt.label}
              </MafButton>
            </MafOptionWrapper>
          )
        })}
      </div>
    </div>
  )
}

const InBurdenAnalysisControls: React.FC = () => {
  const [membershipFilters, setMembershipFilters] = useRecoilState(membershipFiltersAtom)
  const multiAnalysisColorBy = useRecoilValue(multiAnalysisColorByAtom)
  return (
    <div className='burden-set'>
      <span>
        <strong>Include variants:</strong>
      </span>
      <label>
        {multiAnalysisColorBy === 'consequence' && (
          <ColorMarker color={consequenceCategoryColors.lof} />
        )}
        <span className='burden-set-text'>pLoF</span>
        <Checkbox
          label=''
          checked={membershipFilters.pLoF}
          id='pLoF-membership'
          disabled={false}
          onChange={(checked: boolean) => {
            setMembershipFilters({ ...membershipFilters, pLoF: checked })
          }}
        />
      </label>
      <label>
        {multiAnalysisColorBy === 'consequence' && (
          <ColorMarker color={consequenceCategoryColors.missense} />
        )}
        <span className='burden-set-text'>Missense</span>
        <Checkbox
          label=''
          checked={membershipFilters['missense']}
          id='missense-membership'
          disabled={false}
          onChange={(checked: boolean) => {
            setMembershipFilters({ ...membershipFilters, missense: checked })
          }}
        />
      </label>
      <label>
        {multiAnalysisColorBy === 'consequence' && (
          <ColorMarker color={consequenceCategoryColors.synonymous} />
        )}
        <span className='burden-set-text'>Synonymous</span>
        <Checkbox
          label=''
          checked={membershipFilters.synonymous}
          disabled={false}
          id='syn-membership'
          onChange={(checked: boolean) => {
            setMembershipFilters({ ...membershipFilters, synonymous: checked })
          }}
        />
      </label>
      <label>
        {multiAnalysisColorBy === 'consequence' && (
          <ColorMarker color={consequenceCategoryColors['non-coding']} />
        )}
        <span className='burden-set-text'>Non-coding</span>
        <Checkbox
          label=''
          checked={membershipFilters['non-coding']}
          disabled={false}
          id='non-coding-membership'
          onChange={(checked: boolean) => {
            setMembershipFilters({ ...membershipFilters, 'non-coding': checked })
          }}
        />
      </label>
    </div>
  )
}

const GwasCatalogOptions: React.FC = () => {
  const [gwasCatalogOption, setGwasCatalogOption] = useRecoilState(gwasCatalogOptionsAtom)
  return (
    <div className='gwas-catalog-options'>
      <label>
        <span>GWAS Catalog</span>
        <SegmentedControl
          id='gwas-catalog-options-control'
          options={[
            { value: 'hide', label: 'Hide' },
            { value: 'highlight', label: 'Highlight' },
            { value: 'filter', label: 'Filter' },
          ]}
          //@ts-ignore FIXME
          value={gwasCatalogOption}
          onChange={setGwasCatalogOption}
        />
      </label>
    </div>
  )
}

const ShowCaseControlTracks: React.FC = () => {
  const [showCaseControlTracks, setShowCaseControlTracks] =
    useRecoilState(showCaseControlTracksAtom)
  return (
    <div className='show-case-control-tracks'>
      <label>
        <span>Show case/control tracks</span>
        <Checkbox
          label=''
          checked={showCaseControlTracks}
          disabled={false}
          id='show-case-control-tracks'
          onChange={(_: boolean) => {
            setShowCaseControlTracks(!showCaseControlTracks)
          }}
        />
      </label>
    </div>
  )
}

const EnableVariantLabels: React.FC = () => {
  const [enableVariantLabels, setEnableVariantLabels] =
    useRecoilState(enableVariantLabelsAtom)
  const [, setVariantLabels] = useRecoilState(variantLabelsAtom)
  const [, setVariantShowLabel] = useRecoilState(variantShowLabelAtom)

  const handleClearLabels = () => {
    setVariantLabels({})      // Clear custom text

    // Explicitly set all active labels to false so they don't reappear via auto-label logic
    setVariantShowLabel(prev => {
      const next = { ...prev }
      Object.keys(next).forEach(k => {
        if (next[k]) {
          next[k] = false;
        }
      })
      return next;
    })
  }

  return (
    <div className='enable-variant-labels' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', gap: '8px', flexWrap: 'wrap' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, flexShrink: 0 }}>
        <span>Enable variant labels</span>
        <Checkbox
          label=''
          checked={enableVariantLabels}
          disabled={false}
          id='enable-variant-labels'
          onChange={(_: boolean) => {
            setEnableVariantLabels(!enableVariantLabels)
          }}
        />
      </label>
      {enableVariantLabels && (
        <Button
          onClick={handleClearLabels}
          style={{
            fontSize: '10px',
            padding: '3px 8px',
            height: 'auto',
            flexShrink: 0,
          }}
        >
          Clear
        </Button>
      )}
    </div>
  )
}

const ColorByControls: React.FC = () => {
  const [multiAnalysisColorBy, setMultiAnalysisColorBy] = useRecoilState(multiAnalysisColorByAtom)
  return (
    <div className='multi-analysis-color-by'>
      <span>
        <strong>Color by</strong>
      </span>
      <SegmentedControl
        id='multi-analysis-color-by'
        options={[
          { value: 'consequence', label: 'CSQ' },
          { value: 'pvalue', label: 'P' },
          { value: 'beta', label: 'Beta' },
          { value: 'analysis', label: 'Trait' },
          { value: 'homozygote', label: 'Hom' },
        ]}
        value={multiAnalysisColorBy}
        onChange={setMultiAnalysisColorBy}
      />
    </div>
  )
}

const VariantSearchInput: React.FC = () => {
  const [searchText, setVariantSearchText] = useRecoilState(variantSearchTextAtom)
  const searchInput = useRef(null)
  return (
    <div className='multi-analysis-variant-search'>
      <SearchInput
        ref={searchInput}
        placeholder='Search variant table'
        value={searchText}
        onChange={(searchText: string) => {
          setVariantSearchText(searchText)
        }}
      />
      <KeyboardShortcut
        keys='/'
        handler={(e: any) => {
          e.preventDefault()
          if (searchInput.current) {
            // @ts-ignore FIXME
            searchInput.current.focus()
          }
        }}
      />
    </div>
  )
}
const PvalueLegend: React.FC = () => {
  const pValFilter = useRecoilValue(autoPvalFilter)
  return (
    <div className='pvalue-legend'>
      <span>
        <strong>Variant P-value coloring</strong>
      </span>
      <br />
      <span>
        <ColorMarker color='white' />
        1.0 &gt;{' '}
        <RoundedNumber
          num={variantYellowThreshold}
          highlightColor={yellowThresholdColor}
        /> &gt; <RoundedNumber num={variantGreenThreshold} highlightColor={greenThresholdColor} />
      </span>
      <br />
      {pValFilter && (
        <span className='auto-pvalue-filter-warning'>
          <TooltipAnchor
            tooltip={`Variants filtered to P-value < ${pValFilter} due to multiple analyses selected. Select fewer analyses to see all P-values`}
          >
            <TooltipHint>
              <Warning height={8} width={8} /> Filter &lt; {pValFilter} applied
            </TooltipHint>
          </TooltipAnchor>
        </span>
      )}
    </div>
  )
}

const AlleleFrequencySlider: React.FC = () => {
  const [alleleFrequencyFilter, setAlleleFrequencyFilter] =
    useRecoilState(alleleFrequencyFilterAtom)
  return (
    <div className='allele-frequency-slider'>
      <label>
        <span>
          <strong>Allele frequency (NFE)</strong>
        </span>
        <div className='display-values'>
          <span className='value'>{RoundedNumber({ num: alleleFrequencyFilter[0] })}</span>
          <span className='value'>{RoundedNumber({ num: alleleFrequencyFilter[1] })}</span>
        </div>
        <RangeSlider
          initialValues={alleleFrequencyFilter}
          presetInterval={[1e-7, 1]}
          onIntervalChange={setAlleleFrequencyFilter}
          step={1e-6}
          updateInterval={1000}
          showInputs={false}
          width={100}
          useLogScale
        />
      </label>
    </div>
  )
}

const AlleleFrequencyLegend: React.FC<{ isRegion?: boolean }> = ({ isRegion }) => {
  const regionId = useRecoilValue(regionIdAtom)
  const alleleFrequencyScale = getAlleleFrequencyScale(
    isRegion ?? (regionId !== null && regionId !== undefined)
  )

  const margin = { left: 5, right: 5 }
  const width = 170

  const height = 30
  const labels = [0.00001, 0.0001, 0.001, 0.01, 0.1]
  const numTicks = labels.length

  return (
    <svg id='allele-frequency-legend' width={width} height={height}>
      {labels.map((tick, i) => {
        const x = margin.left + (i / numTicks) * width
        return (
          <g key={`af-circle-group-${i}`}>
            <text x={x - 5} y={height * 0.33} fontSize='8px' color='lightgrey'>
              {tick.toExponential(1)}
            </text>
            <circle
              key={`af-circle-${i}`}
              cx={x + 5}
              cy={height * 0.66}
              r={alleleFrequencyScale(tick)}
              fill='grey'
              stroke='var(--theme-border, black)'
              strokeWidth={1}
            />
          </g>
        )
      })}
    </svg>
  )
}

const TransparencySlider: React.FC = () => {
  const [multiAnalysisTransparency, setMultiAnalysisTransparency] = useRecoilState(
    multiAnalysisTransparencyAtom
  )
  return (
    <div className='transparency-slider'>
      <label>
        <span>
          <strong>Transparency</strong>
        </span>
        <RangeSlider
          presetInterval={[0, 1]}
          initialValues={multiAnalysisTransparency}
          onIntervalChange={setMultiAnalysisTransparency}
          step={0.01}
          updateInterval={1000}
          showInputs={false}
          hideLowerBound
          width={100}
        />
      </label>
    </div>
  )
}

const TableFormatControls: React.FC = () => {
  const [tableFormat, setTableFormat] = useRecoilState(multiAnalysisVariantTableFormatAtom)
  return (
    <div className='table-format'>
      <span>
        <strong>Table format</strong>
      </span>
      <SegmentedControl
        id='multi-analysis-table-format'
        options={[
          { value: 'long', label: 'Long' },
          { value: 'wide', label: 'Wide' },
        ]}
        value={tableFormat}
        onChange={setTableFormat}
      />
    </div>
  )
}

const FieldGroupControls: React.FC = () => {
  const variantColumnGroup = useRecoilValue(variantFieldGroupState)
  const setSelectedColumns = useSelectedVariantFieldsPreset()
  return (
    <div className='field-group-controls'>
      <span>
        <strong>Column presets</strong>
      </span>
      <SegmentedControl
        id='field-group-controls1'
        options={[
          { value: 'counts', label: 'Counts' },
          { value: 'freq', label: 'Freq' },
          { value: 'pop', label: 'Pop' },
        ]}
        value={variantColumnGroup}
        onChange={(value: any) => setSelectedColumns(value)}
      />
      <SegmentedControl
        id='field-group-controls2'
        options={[
          { value: 'categorical_default', label: 'Categorical' },
          { value: 'continuous_default', label: 'Continuous' },
        ]}
        //@ts-ignore FIXME
        value={variantColumnGroup}
        onChange={(value: any) => setSelectedColumns(value)}
      />
      <SegmentedControl
        id='field-group-controls3'
        options={[
          { value: 'stat', label: 'Stat' },
          { value: 'all', label: 'All' },
          { value: 'none', label: 'None' },
        ]}
        //@ts-ignore FIXME
        value={variantColumnGroup}
        onChange={(value: any) => setSelectedColumns(value)}
      />
    </div>
  )
}

const IndividualFieldCheckboxes: React.FC = () => {
  const selectedVariantFields = useRecoilValue(selectedVariantFieldsAtom)
  const ancestryGroup = useRecoilValue(ancestryGroupAtom)
  const enableVariantLabels = useRecoilValue(enableVariantLabelsAtom)
  const toggleField = useToggleSelectedVariantField()
  const columns = getVariantColumns({ columns: selectedVariantFieldsOptions, ancestryGroup })

  // Filter out label columns if feature flag is disabled
  const filteredColumns = columns.filter((opt) => {
    if (!enableVariantLabels && (opt.key === 'show_label' || opt.key === 'label')) {
      return false
    }
    return true
  })

  return (
    <div className='individual-field-checkboxes'>
      <span>
        <strong>Columns</strong>
      </span>
      {filteredColumns.map((opt) => (
        <div key={opt.key} className='individual-checkbox'>
          <Checkbox
            label=''
            id={`multi-analysis-compare-fields-${opt.key}`}
            checked={selectedVariantFields.includes(opt.key as VariantFieldType)}
            disabled={false}
            onChange={() => toggleField(opt.key as VariantFieldType)}
          />
          <TooltipAnchor
            // @ts-expect-error ts-migrate(2322) FIXME
            tooltip={opt.tooltip}
          >
            <span>{opt.heading}</span>
          </TooltipAnchor>
        </div>
      ))}
    </div>
  )
}

const UnselectVariant: React.FC = () => {
  const setVariantSearchText = useSetRecoilState(variantSearchTextAtom)
  const { clearVariant } = useAppNavigation()

  return (
    <Button
      style={{ backgroundColor: 'var(--theme-primary)', color: 'white', fontWeight: 'bold', width: '100%' }}
      onClick={() => {
        setVariantSearchText('')
        clearVariant()
      }}
    >
      Clear Selected Variant
    </Button>
  )
}

export const GenePageControls = ({
  embedded = false,
  contextKind,
}: {
  embedded?: boolean
  contextKind?: 'gene' | 'locus'
}) => {
  const variantId = useRecoilValue(variantIdAtom)
  const regionId = useRecoilValue(regionIdAtom)
  const setHideGeneOpts = useSetRecoilState(hideGeneOptsAtom)
  const effectiveRegionId = contextKind === 'gene' ? null : regionId
  const optionsLabel = getDetailOptionsLabel({ variantId, regionId: effectiveRegionId })
  const hideOptionsLabel = `Hide ${optionsLabel}`

  const tableFormat = useRecoilValue(multiAnalysisVariantTableFormatAtom)

  const GenePageControlsItems: React.FC = () => {
    return (
      <>
        <ControlsHeaderWrapper>
          <ControlsHeaderTitle>{optionsLabel}</ControlsHeaderTitle>
          <ControlsCloseButton
            type="button"
            onClick={() => setHideGeneOpts(true)}
            title={hideOptionsLabel}
            aria-label={hideOptionsLabel}
            aria-expanded={true}
            aria-controls={optionPanelContract.detail.id}
          >
            <span aria-hidden="true">›</span>
          </ControlsCloseButton>
        </ControlsHeaderWrapper>
        <MafSelector />
        <InBurdenAnalysisControls />
        {/* <GwasCatalogOptions /> */}
        {/* <ShowCaseControlTracks /> */}
        <EnableVariantLabels />
        <ColorByControls />
        <VariantSearchInput />
        <PvalueLegend />
        {/* <TransparencySlider /> */}
        {/* <AlleleFrequencySlider /> */}
        <AlleleFrequencyLegend isRegion={contextKind === 'locus'} />
        {/* <TableFormatControls /> */}
        <FieldGroupControls />
        <IndividualFieldCheckboxes />
        {/* <DataStats /> */}
      </>
    )
  }

  const VariantPageControls: React.FC = () => {
    return (
      <>
        <ControlsHeaderWrapper>
          <ControlsHeaderTitle>{optionsLabel}</ControlsHeaderTitle>
          <ControlsCloseButton
            type="button"
            onClick={() => setHideGeneOpts(true)}
            title={hideOptionsLabel}
            aria-label={hideOptionsLabel}
            aria-expanded={true}
            aria-controls={optionPanelContract.detail.id}
          >
            <span aria-hidden="true">›</span>
          </ControlsCloseButton>
        </ControlsHeaderWrapper>
        <UnselectVariant />
        <InBurdenAnalysisControls />
        {/* <ShowCaseControlTracks /> */}
        <EnableVariantLabels />
        <ColorByControls />
        {/* <TransparencySlider /> */}
        {/* <TableFormatControls /> */}
        {tableFormat === 'long' && (
          <>
            <FieldGroupControls />
            <IndividualFieldCheckboxes />
          </>
        )}
      </>
    )
  }

  if (variantId) {
    return (
      <GenePageControlStylesVariantFocus
        id={optionPanelContract.detail.id}
        $embedded={embedded}
      >
        <VariantPageControls />
      </GenePageControlStylesVariantFocus>
    )
  }

  return (
    <GenePageControlsGeneFocus id={optionPanelContract.detail.id} $embedded={embedded}>
      <GenePageControlsItems />
    </GenePageControlsGeneFocus>
  )
}
